import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Settings, UserCog, LogOut, Plus, Search } from 'lucide-react';
import { PANEL_BUTTONS } from './IconRail';
import { avatarInitials } from '../utils/avatar';
import { useLanguage } from '../i18n';

/**
 * The "App-Like" overlay menu (Mobile Visual #171) — mobile only.
 *
 * Covers 75% of the phone's horizontal width as a slightly-transparent drawer
 * so the underlying conversation stays visible both THROUGH the drawer and in
 * the remaining 25% to its right. A clear X in the top corner dismisses it, as
 * does tapping the 25% strip (the backdrop).
 *
 * Internal split:
 *   Left  — the PAI feature list (Documents, Calendar, Kanban, Files, …) plus
 *           Settings + the account/logout controls that live in the desktop
 *           icon rail.
 *   Right — the active conversation list (+ New Chat).
 *
 * Selecting a feature OR a conversation closes the overlay and "pops" that
 * selection into the full-screen view (the parent wires onSelect* to do both).
 *
 * Rendered through a portal at `z-[60]` so it floats above the full-screen chat
 * and any open panel (z-20). The whole thing is `shell:hidden`, a structural
 * guarantee it can never appear on the preserved desktop layout.
 */
export default function MobileMenuOverlay({
    open,
    onClose,
    activePanel = null,
    unseenPanels = [],
    onSelectFeature,
    conversations = [],
    currentConversationId = null,
    onSelectConversation,
    onNewChat,
    onManageConversations,
    onOpenSettings,
    onOpenLauncher,
    user = null,
    onLogout,
}) {
    const { t } = useLanguage();

    // Esc closes the overlay (mirrors the panels / dialogs).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const initials = avatarInitials(user);

    return createPortal(
        <div className="fixed inset-0 z-[60] shell:hidden" data-testid="mobile-menu-overlay">
            {/* Backdrop — transparent so the 25% strip shows the live conversation
                clearly; tapping it closes the menu. */}
            <div
                className="absolute inset-0"
                data-testid="mobile-menu-backdrop"
                onClick={onClose}
            />

            {/* The 75% drawer — slightly see-through (translucent bg + subtle blur)
                so the conversation behind stays visible, signalling it's still there. */}
            <div
                className="absolute left-0 top-0 bottom-0 w-3/4 max-w-[320px] flex flex-col bg-pai-modal/95 backdrop-blur-sm shadow-modal border-r border-pai-subtle animate-slide-in-left"
                data-testid="mobile-menu-drawer"
            >
                {/* Header row with the close X in the top corner */}
                <div className="flex items-center justify-between px-3 py-3 border-b border-pai-subtle flex-shrink-0">
                    {/* #512: the logo + "Menu" header is the Home button — it closes the
                        menu and returns to the Main Page (the launcher), like the desktop
                        rail logo. */}
                    <button
                        type="button"
                        onClick={() => { onClose?.(); onOpenLauncher?.(); }}
                        data-testid="mobile-menu-home"
                        aria-label={t('navLauncher', 'PAI — all features')}
                        className="flex items-center gap-2 -ml-1 pl-1 pr-2 py-1 rounded-lg transition-colors hover:bg-white/[0.07] active:bg-white/[0.12] active:scale-[0.97]"
                    >
                        <span className="w-8 h-8 rounded-full bg-white text-pai-void flex items-center justify-center font-bold text-[11px] select-none">
                            PAI
                        </span>
                        <span className="text-sm font-semibold text-white">{t('menu', 'Menu')}</span>
                    </button>
                    <button
                        onClick={onClose}
                        data-testid="mobile-menu-close"
                        aria-label={t('close', 'Close')}
                        className="p-1.5 text-pai-meta hover:text-white hover:bg-white/[0.07] rounded-lg transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* The split: features (left) | conversations (right) */}
                <div className="flex-1 flex min-h-0">
                    {/* Left — PAI features + settings/account */}
                    <div
                        className="w-1/2 min-w-[104px] flex flex-col border-r border-pai-subtle"
                        data-testid="mobile-menu-features"
                    >
                        <div className="flex-1 min-h-0 overflow-y-auto py-2">
                            {PANEL_BUTTONS.map(({ name, Icon, label }) => {
                                const active = activePanel === name;
                                return (
                                    <button
                                        key={name}
                                        data-testid={`mobile-menu-feature-${name}`}
                                        onClick={() => onSelectFeature?.(name)}
                                        className={`w-full min-h-[40px] flex items-center gap-2 px-2.5 py-2 text-left text-[13px] leading-tight transition-colors ${
                                            active
                                                ? 'border-white/80 bg-white/[0.055] text-white font-semibold'
                                                : 'text-pai-secondary hover:bg-white/[0.07]'
                                        }`}
                                    >
                                        <span className="relative flex-shrink-0 flex">
                                            <Icon size={16} />
                                            {unseenPanels.includes(name) && (
                                                <span
                                                    data-testid={`unseen-dot-${name}`}
                                                    aria-hidden="true"
                                                    className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-pai-red"
                                                />
                                            )}
                                        </span>
                                        {/* #432: wrap, don't truncate — at 104px a long two-feature label lost half itself. */}
                                        <span className="min-w-0 break-words">{t(name, label)}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {/* System + User Settings + logout — the bottom of the desktop
                            rail, mirroring the #236 split: the gear entry opens System
                            Settings; the account bubble opens User Settings. */}
                        <div className="border-t border-pai-subtle py-2">
                            <button
                                data-testid="mobile-menu-settings"
                                onClick={() => { onClose?.(); onOpenSettings?.('system'); }}
                                className="w-full min-h-[40px] flex items-center gap-2 px-2.5 py-2 text-left text-[13px] text-pai-secondary hover:bg-white/[0.07] transition-colors"
                            >
                                <Settings size={16} className="flex-shrink-0" />
                                <span className="min-w-0 break-words leading-tight">{t('systemSettings', 'System Settings')}</span>
                            </button>
                            {onLogout && (
                                <button
                                    data-testid="mobile-menu-logout"
                                    onClick={() => { onClose?.(); onLogout?.(); }}
                                    className="w-full min-h-[40px] flex items-center gap-2 px-2.5 py-2 text-left text-[13px] text-pai-secondary hover:bg-white/[0.07] transition-colors"
                                >
                                    <LogOut size={16} className="flex-shrink-0" />
                                    <span className="truncate">{t('logout', 'Logout')}</span>
                                </button>
                            )}
                            {/* Account bubble = the User Settings entry (opens the
                                account-specific view). */}
                            <button
                                data-testid="mobile-menu-user-settings"
                                onClick={() => { onClose?.(); onOpenSettings?.('user'); }}
                                className="w-full min-h-[44px] flex items-center gap-2 px-2.5 pt-2 mt-1 pb-1 border-t border-pai-subtle text-left hover:bg-white/[0.07] transition-colors"
                                title={t('settingsTitle', 'Settings')}
                            >
                                <div className="w-7 h-7 rounded-full bg-white text-pai-void flex items-center justify-center text-[11px] font-semibold select-none flex-shrink-0">
                                    {initials}
                                </div>
                                <span className="flex items-center gap-1 text-[11px] text-pai-meta truncate">
                                    <UserCog size={12} className="flex-shrink-0" />
                                    {t('settingsTitle', 'Settings')}
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Right — active conversations */}
                    <div
                        className="flex-1 min-w-0 flex flex-col"
                        data-testid="mobile-menu-conversations"
                    >
                        <div className="p-2 flex-shrink-0">
                            <button
                                data-testid="mobile-menu-new-chat"
                                onClick={onNewChat}
                                className="w-full flex items-center gap-2 px-2.5 py-2 text-[13px] font-medium text-pai-secondary bg-white/[0.05] border border-pai rounded-lg hover:bg-white/[0.07] transition-colors"
                            >
                                <Plus size={15} className="flex-shrink-0" />
                                <span className="truncate">{t('newChat', 'New Chat')}</span>
                            </button>
                            {/* #432: the overlay lists conversations, but search and the
                                per-conversation actions (rename, data, export, delete)
                                live in the full sidebar — reachable here as a sheet. */}
                            {onManageConversations && (
                                <button
                                    data-testid="mobile-menu-manage-conversations"
                                    onClick={onManageConversations}
                                    className="mt-1.5 w-full min-h-[40px] flex items-center gap-2 px-2.5 py-2 text-[13px] font-medium text-pai-secondary rounded-lg hover:bg-white/[0.07] transition-colors"
                                >
                                    <Search size={15} className="flex-shrink-0" />
                                    <span className="min-w-0 break-words leading-tight text-left">{t('navSearchAndManage', 'Search & manage')}</span>
                                </button>
                            )}
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-0.5">
                            {conversations.length === 0 ? (
                                <div className="px-2 py-6 text-center text-[12px] text-pai-faint">
                                    {t('noConversations', 'No conversations yet')}
                                </div>
                            ) : (
                                conversations.map((conv) => {
                                    const active = conv.id === currentConversationId;
                                    return (
                                        <button
                                            key={conv.id}
                                            data-testid="mobile-menu-conversation"
                                            onClick={() => onSelectConversation?.(conv)}
                                            className={`w-full min-h-[40px] flex items-center gap-2 px-2.5 py-2 text-left text-[13px] rounded-lg transition-colors ${
                                                active
                                                    ? 'border-white/80 bg-white/[0.055] text-white font-semibold'
                                                    : 'text-pai-secondary hover:bg-white/[0.07]'
                                            }`}
                                        >
                                            {/* Speech-bubble icon removed for parity with
                                                the desktop sidebar (Conversation Icon #239)
                                                — only the title shows. */}
                                            <span className="truncate">{conv.title || t('untitledChat', 'New conversation')}</span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
